import { AlertTriangle, ShieldCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { cn } from '@/lib/cn';
import type {
  DecisionIntelligence,
  PairingReach,
  Proposition,
  SimilarDecision,
} from '@/services/types';

/**
 * What the corpus knows ABOUT one decision: the legal propositions extracted from
 * its reasoning, and the comparable decisions it was paired with.
 *
 * Reach differs sharply and the panel says so rather than rendering an empty box.
 * The share the "not paired" caveat quotes comes from the payload's `pairing`
 * reach, measured per request — a decision with no comparator has not "no
 * similar cases", it has no PAIR, which is a statement about the
 * citation-overlap threshold, not about the corpus.
 */
export function CaseIntelligence({
  intelligence,
  pairing,
}: {
  intelligence: DecisionIntelligence;
  pairing: PairingReach;
}) {
  const { t, i18n } = useTranslation(['anatel']);
  const { propositions, similar, similar_total: similarTotal } = intelligence;
  const percent = new Intl.NumberFormat(i18n.language, {
    style: 'percent',
    maximumFractionDigits: 1,
  });

  return (
    <div className="space-y-3 border-t pt-3">
      {propositions.length > 0 ? (
        <section className="space-y-2">
          <h4 className="text-xs font-medium text-foreground">
            {t('anatel:intelligence.propositions', {
              n: propositions.length,
              verified: intelligence.propositions_verified,
            })}
          </h4>
          <ul className="space-y-2">
            {propositions.map((proposition, index) => (
              <li key={`${proposition.issue}-${index}`}>
                <PropositionRow proposition={proposition} />
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <section className="space-y-2">
        <h4 className="text-xs font-medium text-foreground">
          {similarTotal > similar.length
            ? t('anatel:intelligence.similarCapped', {
                shown: similar.length,
                total: similarTotal,
              })
            : t('anatel:intelligence.similar', { n: similarTotal })}
        </h4>
        {similar.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            {t('anatel:intelligence.noSimilar', {
              pct:
                pairing.final_decisions > 0
                  ? percent.format(pairing.paired_finals / pairing.final_decisions)
                  : '—',
            })}
          </p>
        ) : (
          <ul className="space-y-1.5">
            {similar.map((match) => (
              <li key={`${match.process_key}-${match.decision_number ?? ''}`}>
                <SimilarRow match={match} />
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function PropositionRow({ proposition }: { proposition: Proposition }) {
  const { t } = useTranslation(['anatel']);
  const affirms = proposition.polarity === 'affirms';

  return (
    <div className="rounded-md border bg-background p-2">
      <div className="flex flex-wrap items-baseline gap-2">
        <span
          className={cn(
            'rounded px-1.5 py-0.5 text-[10px] font-medium',
            affirms
              ? 'bg-primary/15 text-foreground'
              : 'bg-muted text-muted-foreground',
          )}
        >
          {affirms ? t('anatel:intelligence.affirms') : t('anatel:intelligence.denies')}
        </span>
        <span className="text-xs text-muted-foreground">{proposition.issue}</span>
      </div>
      <p className="mt-1 text-xs text-foreground">{proposition.proposition_text}</p>
      {proposition.legal_basis ? (
        <p className="mt-0.5 text-[11px] text-muted-foreground">{proposition.legal_basis}</p>
      ) : null}
      {/* An unverified span was quoted by the model but not found verbatim in the
          source. It is shown demoted and labelled, never as a citation. */}
      <blockquote
        className={cn(
          'mt-1.5 border-l-2 pl-2 text-[11px] italic',
          proposition.evidence_verified
            ? 'border-primary/40 text-muted-foreground'
            : 'border-destructive/50 text-destructive',
        )}
      >
        <span className="mb-0.5 flex items-center gap-1 not-italic">
          {proposition.evidence_verified ? (
            <>
              <ShieldCheck className="h-3 w-3" aria-hidden />
              {t('anatel:consistency.evidenceVerified')}
            </>
          ) : (
            <>
              <AlertTriangle className="h-3 w-3" aria-hidden />
              {t('anatel:consistency.evidenceUnverified')}
            </>
          )}
        </span>
        {proposition.evidence_span}
      </blockquote>
    </div>
  );
}

function SimilarRow({ match }: { match: SimilarDecision }) {
  const { t, i18n } = useTranslation(['anatel']);
  const currency = new Intl.NumberFormat(i18n.language, { style: 'currency', currency: 'BRL' });

  return (
    <div
      className={cn(
        'flex flex-wrap items-center justify-between gap-2 rounded-md border p-2 text-xs',
        match.verdict === 'CONTRADICTION' ? 'border-amber-500/50 bg-amber-500/5' : 'bg-background',
      )}
    >
      <span className="flex min-w-0 flex-wrap items-center gap-2">
        <Link
          to={`/anatel/p/${match.process_key}`}
          className="font-medium text-primary underline-offset-4 hover:underline"
        >
          {match.process_number}
        </Link>
        {match.outcome_codes.map((code) => (
          <span key={code} className="rounded border px-1.5 py-0.5 text-[11px] text-foreground">
            {t(`anatel:overview.outcomes.${code}`, { defaultValue: code })}
          </span>
        ))}
        {match.same_outcome === false ? (
          <span className="text-[11px] font-medium text-amber-700 dark:text-amber-300">
            {t('anatel:intelligence.differentOutcome')}
          </span>
        ) : null}
        {match.verdict === 'CONTRADICTION' ? (
          <span className="rounded border border-amber-500/40 bg-amber-500/10 px-1.5 py-0.5 text-[10px] text-amber-700 dark:text-amber-300">
            {t('anatel:consistency.potentialInconsistency')}
          </span>
        ) : null}
      </span>
      <span className="shrink-0 tabular-nums text-muted-foreground">
        {match.fine_brl !== null ? `${currency.format(match.fine_brl)} · ` : ''}
        {t('anatel:intelligence.sharedCitations', { n: match.shared_count })}
      </span>
    </div>
  );
}
